import { Modal } from '@platform/components';
import { useEffect, useRef } from 'react';
import Player from '@vimeo/player';
import * as Sentry from '@sentry/react';
import { UserNextBestActions } from '@platform/client';
import EVENTS from '../../analytics/events';
import mixpanel from 'mixpanel-browser';

// This value matches the legacy app's
const LOCALSTORAGE_WATCHTIME_KEY = 'vimeo_COMPLETE_ONBOARDING';

/** Roughly adapted from movingworlds/assets/scripts/dashboard/dashboard.js */
const VimeoOnboardingVideo = ({
  url,
  isOpen,
  setIsOpen,
  engagementActionId,
}: {
  url: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  engagementActionId: string;
}) => {
  const dismissEngagement = UserNextBestActions.useDismissOnboardVideo();
  const playerRef = useRef<Player | null>(null);

  // Initialize the Vimeo player only whenthe modal is visible
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    playerRef.current = new Player('video-player-target', {
      url: url,
      autoplay: false,
      width: 640,
      height: 360,
      pip: true,
    });

    const currentWatchTime = localStorage.getItem(LOCALSTORAGE_WATCHTIME_KEY) ?? '0';
    playerRef.current.setCurrentTime(parseInt(currentWatchTime, 10));

    return () => {
      playerRef.current?.destroy();
    };
  }, [isOpen, url]);

  /** Hide the modal and record the current user's playback location into localstorage
   * Also trigger marking the NBA as complete if the user has watched the video to the end */
  const onModalClose = async () => {
    // In some cases loading the Vimeo player or video can fail, at which point playerRef.current is not
    // null but the relevant Vimeo API calls will throw an error
    try {
      const currentTime = await playerRef.current?.getCurrentTime();
      if (currentTime) {
        localStorage.setItem(LOCALSTORAGE_WATCHTIME_KEY, currentTime.toString());

        const videoDuration = await playerRef?.current?.getDuration();

        // If less than 30 seconds are left in the video, mark it as finished
        if (videoDuration && videoDuration - currentTime < 30) {
          // Note that the legacy app sends a message to Zoho here. However, this seems to only fire for
          // org rep users and thus is excluded here until we revisit for org reps
          // https://bitbucket.org/movingworlds/movingworlds/src/f3265c6f7fe4ef416425aa4975a1fab9471e1c75/movingworlds/plugin/api/engagementAction/engagementAction.js#lines-81

          // TODO: We also send 'Dismiss EngagementAction' and 'Click EngagementAction' events through Segment for _all_ NBA dismissals, even this programmatic one.
          // I've omitted this in this new app due to avoiding use of Segment. I'm currently asking Grace whether we need so let's revisit, but for now I'm
          // _just_ dismissing this NBA through the common api endpoint
          dismissEngagement.mutate();
          mixpanel.track(EVENTS.DASHBOARD.HOME.NBA_DISMISSED_VIA_INTRO_VIDEO, {
            engagementActionId: engagementActionId,
          });
        }
      }
    } catch (e) {
      Sentry.captureException(e);
    }

    setIsOpen(false);
  };

  return (
    <Modal
      headerDivision='divider'
      variant='dialog'
      title=''
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      primaryCTA={{
        text: 'Close',
        callback: onModalClose,
      }}
      panelClassName='min-w-[672px]'
      ignoreExtraPadding
    >
      {/* Force reserving sufficient space for the video to avoid the modal visibly resizing */}
      <div className='h-[360px]' id='video-player-target'></div>
    </Modal>
  );
};

export default VimeoOnboardingVideo;
